import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, X, Search } from "lucide-react";

const EMPTY = {
  displayName: "", alias: "", primarySmtp: "", aliases: [], hiddenFromGal: false,
  sentAsCopy: false, sendOnBehalfCopy: false, sendOnBehalf: [], fullAccess: [], fullAccessMap: {}, sendAs: [],
  forwarding: { enabled: false, smtp: "", keepCopy: true },
  autoReply: { state: "Disabled", start: "", end: "", internal: "", external: "", audience: "All" },
};

const MBX_TYPES = [
  { value: "shared", label: "Shared mailbox" },
  { value: "room", label: "Room mailbox" },
  { value: "equipment", label: "Equipment mailbox" },
];

function Chips({ items, onRemove, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {items.map((v) => (
        <span key={v} className="flex items-center gap-1 border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-xs text-zinc-700">
          {v}<button onClick={() => onRemove(v)} className="text-zinc-400 hover:text-red-600"><X size={12} /></button>
        </span>
      ))}
      {items.length === 0 && <span className="text-xs text-zinc-400">—</span>}
    </div>
  );
}

function UserPicker({ users, selected, onChange, testid }) {
  const [q, setQ] = useState("");
  const matches = useMemo(() => !q ? [] : users
    .filter((u) => `${u.displayName} ${u.userPrincipalName}`.toLowerCase().includes(q.toLowerCase()) && !selected.includes(u.userPrincipalName))
    .slice(0, 6), [q, users, selected]);
  return (
    <div className="space-y-2">
      <Chips items={selected} onRemove={(v) => onChange(selected.filter((x) => x !== v))} testid={`${testid}-chips`} />
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" data-testid={`${testid}-search`} className="rounded-sm border-zinc-300 pl-8 text-sm" />
        {matches.length > 0 && (
          <div className="absolute z-50 mt-1 w-full border border-zinc-200 bg-white shadow-lg">
            {matches.map((u) => (
              <button key={u.id} onClick={() => { onChange([...selected, u.userPrincipalName]); setQ(""); }}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-zinc-50">
                <span className="font-medium text-zinc-800">{u.displayName}</span>
                <span className="font-mono text-[11px] text-zinc-400">{u.userPrincipalName}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function SharedMailboxDialog({ mode, mailbox, domains, open, onOpenChange, onSaved }) {
  const { t } = useLang();
  const isEdit = mode === "edit";
  const [s, setS] = useState(EMPTY);
  const [domain, setDomain] = useState("");
  const [mtype, setMtype] = useState("shared");
  const [aliasInput, setAliasInput] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/users").then((r) => setUsers(r.data.filter((u) => !u.isShared && u.userPrincipalName))).catch(() => {});
    if (isEdit && mailbox) {
      setLoading(true);
      api.get(`/shared-mailboxes/${mailbox.id}/settings`)
        .then((r) => setS({ ...EMPTY, ...r.data, fullAccessMap: Object.fromEntries((r.data.fullAccess || []).map((u) => [u, (r.data.fullAccessAutoMap || {})[u] !== false])), forwarding: { enabled: !!(r.data.forwarding?.smtp), ...EMPTY.forwarding, ...r.data.forwarding }, autoReply: { ...EMPTY.autoReply, ...r.data.autoReply } }))
        .catch((e) => toast.error(apiErr(e)))
        .finally(() => setLoading(false));
    } else {
      setS(EMPTY); setMtype("shared"); setDomain(((domains.find((d) => d.default) || domains[0]) || {}).name || ""); setAliasInput("");
    }
  }, [open, isEdit, mailbox]);

  const up = (patch) => setS((cur) => ({ ...cur, ...patch }));
  const upFwd = (patch) => setS((cur) => ({ ...cur, forwarding: { ...cur.forwarding, ...patch } }));
  const upAr = (patch) => setS((cur) => ({ ...cur, autoReply: { ...cur.autoReply, ...patch } }));
  const addAlias = () => { const v = aliasInput.trim(); if (v && !s.aliases.includes(v)) { up({ aliases: [...s.aliases, v] }); setAliasInput(""); } };

  const save = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        const payload = { ...s, fullAccess: s.fullAccess.map((u) => ({ user: u, autoMapping: s.fullAccessMap?.[u] !== false })) };
        const { data } = await api.put(`/shared-mailboxes/${mailbox.id}/settings`, payload);
        if (data.warnings?.length) toast.warning(`Saved with notes: ${data.warnings.join("; ")}`);
        else toast.success(t("shared.updated"));
      } else {
        if (!s.displayName.trim() || !s.alias.trim() || !domain) { toast.error(t("shared.needName")); setBusy(false); return; }
        const primary = `${s.alias.trim()}@${domain}`;
        await api.post("/shared-mailboxes", {
          name: s.alias.trim(), displayName: s.displayName.trim(), primarySmtp: primary,
          aliases: s.aliases, hiddenFromGal: s.hiddenFromGal,
          sentAsCopy: s.sentAsCopy, sendOnBehalfCopy: s.sendOnBehalfCopy, mailboxType: mtype,
        });
        toast.success(t("shared.created"));
      }
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">{isEdit ? t("shared.editTitle") : t("shared.create")}</DialogTitle>
          <DialogDescription>{isEdit ? (mailbox?.mail || "") : t("shared.fieldName")}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="mr-2 animate-spin" /> {t("common.loading")}</div>
        ) : (
          <Tabs defaultValue="general" className="py-2">
            <TabsList className="mb-4 flex w-full flex-wrap justify-start rounded-sm">
              <TabsTrigger value="general" data-testid="sm-tab-general" className="rounded-sm">{t("shared.tabGeneral")}</TabsTrigger>
              <TabsTrigger value="aliases" data-testid="sm-tab-aliases" className="rounded-sm">{t("shared.tabAliases")}</TabsTrigger>
              {isEdit && <TabsTrigger value="delegation" data-testid="sm-tab-delegation" className="rounded-sm">{t("shared.tabDelegation")}</TabsTrigger>}
              {isEdit && <TabsTrigger value="forwarding" data-testid="sm-tab-forwarding" className="rounded-sm">{t("shared.tabForwarding")}</TabsTrigger>}
              {isEdit && <TabsTrigger value="autoreply" data-testid="sm-tab-autoreply" className="rounded-sm">{t("shared.tabAutoReply")}</TabsTrigger>}
            </TabsList>

            <TabsContent value="general" className="space-y-4">
              {!isEdit && (
                <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Mailbox type</Label>
                  <Select value={mtype} onValueChange={setMtype}>
                    <SelectTrigger className="rounded-sm border-zinc-300" data-testid="sm-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{MBX_TYPES.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`sm-type-${m.value}`}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shared.fieldDisplay")}</Label>
                <Input value={s.displayName} onChange={(e) => up({ displayName: e.target.value })} data-testid="sm-display" className="rounded-sm border-zinc-300" /></div>
              {!isEdit ? (
                <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shared.fieldEmail")}</Label>
                  <div className="flex items-center gap-2">
                    <Input value={s.alias} onChange={(e) => up({ alias: e.target.value })} data-testid="sm-alias" placeholder="hr-team" className="rounded-sm border-zinc-300 font-mono text-sm" />
                    <span className="text-zinc-400">@</span>
                    <Select value={domain} onValueChange={setDomain}>
                      <SelectTrigger className="w-56 rounded-sm border-zinc-300 font-mono text-sm" data-testid="sm-domain"><SelectValue placeholder="domain" /></SelectTrigger>
                      <SelectContent>{domains.map((d) => <SelectItem key={d.name} value={d.name}>{d.name}{d.default ? " (default)" : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shared.colEmail")}</Label>
                  <Input value={s.primarySmtp} disabled className="rounded-sm border-zinc-200 bg-zinc-50 font-mono text-sm text-zinc-500" /></div>
              )}
              <label className="flex items-center justify-between border border-zinc-200 p-3"><span className="text-sm text-zinc-700">{t("shared.showInGal")}</span>
                <Switch checked={!s.hiddenFromGal} onCheckedChange={(v) => up({ hiddenFromGal: !v })} data-testid="sm-gal" /></label>
              <label className="flex items-center justify-between border border-zinc-200 p-3"><span className="text-sm text-zinc-700">{t("shared.copySentAs")}</span>
                <Switch checked={s.sentAsCopy} onCheckedChange={(v) => up({ sentAsCopy: v })} data-testid="sm-sentas" /></label>
              <label className="flex items-center justify-between border border-zinc-200 p-3"><span className="text-sm text-zinc-700">{t("shared.copySendOnBehalf")}</span>
                <Switch checked={s.sendOnBehalfCopy} onCheckedChange={(v) => up({ sendOnBehalfCopy: v })} data-testid="sm-sob-copy" /></label>
            </TabsContent>

            <TabsContent value="aliases" className="space-y-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shared.aliasesLabel")}</Label>
              <Chips items={s.aliases} onRemove={(v) => up({ aliases: s.aliases.filter((x) => x !== v) })} testid="sm-aliases" />
              <div className="flex gap-2">
                <Input value={aliasInput} onChange={(e) => setAliasInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAlias())} placeholder="alias@contoso.com" data-testid="sm-alias-input" className="rounded-sm border-zinc-300 font-mono text-sm" />
                <Button variant="outline" onClick={addAlias} data-testid="sm-alias-add" className="rounded-sm border-zinc-300"><Plus size={15} /></Button>
              </div>
            </TabsContent>

            <TabsContent value="delegation" className="space-y-5">
              <div><Label className="mb-2 block text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shared.fullAccess")}</Label>
                <UserPicker users={users} selected={s.fullAccess} onChange={(v) => up({ fullAccess: v })} testid="sm-fullaccess" />
                {s.fullAccess.length > 0 && (
                  <div className="mt-2.5 space-y-1.5" data-testid="sm-automap-list">
                    {s.fullAccess.map((u) => (
                      <div key={u} className="flex items-center justify-between border border-zinc-200 px-3 py-2">
                        <span className="truncate font-mono text-[11px] text-zinc-600">{u}</span>
                        <label className="ml-3 flex shrink-0 items-center gap-2 text-[11px] text-zinc-500">
                          Auto-map in Outlook
                          <Switch checked={s.fullAccessMap?.[u] !== false}
                            onCheckedChange={(val) => up({ fullAccessMap: { ...s.fullAccessMap, [u]: val } })}
                            data-testid={`sm-automap-${u}`} />
                        </label>
                      </div>
                    ))}
                    <p className="text-[11px] text-zinc-400">Auto-mapping applies when access is first granted; toggle it before saving a new delegate.</p>
                  </div>
                )}
              </div>
              <div><Label className="mb-2 block text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shared.sendAs")}</Label>
                <UserPicker users={users} selected={s.sendAs} onChange={(v) => up({ sendAs: v })} testid="sm-sendas" /></div>
              <div><Label className="mb-2 block text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shared.sendOnBehalf")}</Label>
                <UserPicker users={users} selected={s.sendOnBehalf} onChange={(v) => up({ sendOnBehalf: v })} testid="sm-sob" /></div>
            </TabsContent>

            <TabsContent value="forwarding" className="space-y-4">
              <label className="flex items-center justify-between border border-zinc-200 p-3"><span className="text-sm text-zinc-700">{t("shared.fwdEnable")}</span>
                <Switch checked={s.forwarding.enabled} onCheckedChange={(v) => upFwd({ enabled: v })} data-testid="sm-fwd-enable" /></label>
              {s.forwarding.enabled && (
                <>
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shared.fwdAddress")}</Label>
                    <Input value={s.forwarding.smtp} onChange={(e) => upFwd({ smtp: e.target.value })} data-testid="sm-fwd-smtp" placeholder="someone@contoso.com" className="rounded-sm border-zinc-300 font-mono text-sm" /></div>
                  <label className="flex items-center justify-between border border-zinc-200 p-3"><span className="text-sm text-zinc-700">{t("shared.fwdKeep")}</span>
                    <Switch checked={s.forwarding.keepCopy} onCheckedChange={(v) => upFwd({ keepCopy: v })} data-testid="sm-fwd-keep" /></label>
                </>
              )}
            </TabsContent>

            <TabsContent value="autoreply" className="space-y-4">
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shared.arState")}</Label>
                <Select value={s.autoReply.state} onValueChange={(v) => upAr({ state: v })}>
                  <SelectTrigger className="rounded-sm border-zinc-300" data-testid="sm-ar-state"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Disabled">{t("shared.arOff")}</SelectItem>
                    <SelectItem value="Enabled">{t("shared.arOn")}</SelectItem>
                    <SelectItem value="Scheduled">{t("shared.arScheduled")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {s.autoReply.state === "Scheduled" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shared.arStart")}</Label>
                    <Input type="datetime-local" value={s.autoReply.start} onChange={(e) => upAr({ start: e.target.value })} data-testid="sm-ar-start" className="rounded-sm border-zinc-300" /></div>
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shared.arEnd")}</Label>
                    <Input type="datetime-local" value={s.autoReply.end} onChange={(e) => upAr({ end: e.target.value })} data-testid="sm-ar-end" className="rounded-sm border-zinc-300" /></div>
                </div>
              )}
              {s.autoReply.state !== "Disabled" && (
                <>
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shared.arInternal")}</Label>
                    <Textarea value={s.autoReply.internal} onChange={(e) => upAr({ internal: e.target.value })} rows={3} data-testid="sm-ar-internal" className="rounded-sm border-zinc-300" /></div>
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shared.arExternal")}</Label>
                    <Textarea value={s.autoReply.external} onChange={(e) => upAr({ external: e.target.value })} rows={3} data-testid="sm-ar-external" className="rounded-sm border-zinc-300" /></div>
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("shared.arAudience")}</Label>
                    <Select value={s.autoReply.audience} onValueChange={(v) => upAr({ audience: v })}>
                      <SelectTrigger className="rounded-sm border-zinc-300" data-testid="sm-ar-audience"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="None">{t("shared.arAudNone")}</SelectItem>
                        <SelectItem value="Known">{t("shared.arAudKnown")}</SelectItem>
                        <SelectItem value="All">{t("shared.arAudAll")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">{t("common.cancel")}</Button>
          <Button onClick={save} disabled={busy || loading} data-testid="sm-save" className="rounded-sm bg-primary hover:bg-[#002480]">
            {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : null}{isEdit ? t("teams.save") : t("shared.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
